/**
 * Which cell a navigation key asks for, from the cell it was pressed in.
 *
 * ── Why this is a decision and not a switch statement ────────────────────────────────────────
 * The set table is a FORM: every cell is a text input, and inside a text input the arrow keys
 * already mean something — they move the caret. So an arrow can only be taken over where the
 * browser's own answer is "nothing happens", and working out where that is, is the whole of this
 * module. Too permissive and a trainer editing "82,5" loses the caret out of the field; too strict
 * and the navigation the owner asked for does not fire on the press that matters.
 *
 * ── The four rules ───────────────────────────────────────────────────────────────────────────
 * · **UP and DOWN always move.** A single-line input's own up/down only sends the caret to one end
 *   of the value — a behaviour nobody reaches for and one Home/End already provide. The COLUMN is
 *   held, because the gesture this serves is filling a column: a trainer runs down the weights.
 * · **LEFT and RIGHT move only from the edge of the text.** Caret inside "82,5"? The arrow belongs
 *   to the caret. Caret already against the start (LEFT) or the end (RIGHT)? The browser would do
 *   nothing, so the press is free and the row's next field takes it.
 * · **A fully selected value counts as standing at BOTH edges.** This is the one rule that is a
 *   judgement rather than a deduction. Arriving in a cell selects it (see `lib/setTableArrows.ts`),
 *   and the spreadsheet feel the owner asked for is that the next arrow moves on rather than
 *   spending itself collapsing the selection. The cost is that someone who deliberately selected the
 *   whole value with the mouse and pressed RIGHT leaves the cell instead of landing at its end —
 *   one press to recover, and rare against the arrival case, which happens on every vertical move.
 *   A PARTIAL selection is left alone: there the browser's collapse is exactly what was meant.
 * · **Any modifier yields.** Alt+Arrow is word-wise motion, Cmd+Arrow is line-wise on macOS, and
 *   Shift+Arrow extends a selection. All three are text editing, none of them are navigation, and
 *   swallowing them would take away an editing gesture to add a movement one.
 *
 * ── An edge HOLDS; it does not hand the key back ─────────────────────────────────────────────
 * Edges do not wrap: the first row's UP and the last row's DOWN go nowhere, because a wrap would put
 * the trainer somewhere they did not ask to be with no visible cause, and Tab already leaves the
 * table in the obvious direction. But "nowhere" is not the same as "not ours", which is why this
 * module answers with three outcomes rather than a nullable address.
 *
 * Once a press is claimed it stays claimed even where the grid ends, because the browser's own
 * answer to UP in a single-line input is to COLLAPSE the selection and drop the caret at the start.
 * Handing an edge press back would therefore undo the very selection that arriving in the cell just
 * made — the trainer presses UP at the top row, nothing appears to happen, and their next digit is
 * appended to the old value instead of replacing it. Found by driving the table, not by reading it.
 * A press we never claimed — a caret mid-value, any modifier — is a different thing entirely and is
 * returned untouched, default behaviour included.
 *
 * ── Enter, Tab and Shift+Tab, added 8 September 2026 ────────────────────────────────────────
 * The mock answers seven keys, not four, and the three this module used to ignore are the ones a
 * trainer filling a table actually presses:
 *
 * · **Enter is DOWN.** `ArrowDown | Enter -> r = min(maxR, r + 1)` in the mock's own handler. The
 *   cell still COMMITS first — `SetCell` answers Enter on the way up and this module is bound on the
 *   bubble — so a weight typed and confirmed is saved and the caret lands under it, which is the
 *   gesture the mock is built around: a trainer runs down a column typing and pressing Enter.
 * · **Tab WALKS, and it wraps by row.** Forward: one column right, and off the last column to the
 *   first column of the row below. Backward: the mirror. The browser's own Tab would visit the type
 *   badge and the two row buttons between every pair of cells, which is not a walk of the values.
 *
 * ── The two extremes hand Tab BACK, and that is a deliberate departure from the mock ─────────
 * The mock CLAMPS at both ends: Tab in the last cell puts the trainer in the first column of the
 * same row, Shift+Tab in the first cell stays put. Reproducing that would make the set table a
 * KEYBOARD TRAP — sixty cells with no key that leaves them — which WCAG 2.1.2 forbids outright, and
 * this panel's only other exit (Escape) is the one that closes the whole day. So the last cell's Tab
 * and the first cell's Shift+Tab are YIELDED: the browser moves to the next control after the table
 * and the trainer is out. Everything between them wraps exactly as the mock does.
 *
 * Shift is the one modifier Tab reads rather than yields to, because Shift+Tab IS the backward walk.
 * Cmd+Tab and Ctrl+Tab never reach a page at all, so a press arriving here with Shift among its
 * modifiers is Shift alone as far as this module can be asked to care.
 *
 * The chain is the EDITABLE cells only — the type badge and the two row buttons are not on it. They
 * are buttons, each is one Tab away, and the badge's own menu binds the arrow keys for itself;
 * putting them on the chain would land an arrow on a control where the next arrow means something
 * else entirely.
 */

/** A cell's address in the table's editable grid. Both are zero-based, row first. */
export interface SetCellAddress {
  readonly row: number;
  readonly column: number;
}

/**
 * How many editable cells there are to move between.
 *
 * The grid is rectangular: `dayProgramming` builds every row's cells from the SAME resolved column
 * list, so column 2 is the same column in every row even when its content differs — a back-off's
 * weight cell prints "% topu" where a working set prints "kg", and both are the load column.
 */
export interface SetGridShape {
  readonly rowCount: number;
  readonly columnCount: number;
}

/** One navigation keypress, as the input under it reports itself. */
export interface ArrowPress {
  readonly key: string;
  /** Meta, Control, Alt or Shift — any of them makes an ARROW the browser's, not ours. */
  readonly hasModifier: boolean;
  /**
   * Shift specifically, which only Tab reads: it is the backward walk rather than a modifier to
   * yield to. Optional, and absent means no — every caller that only ever sends arrows is unchanged.
   */
  readonly hasShift?: boolean;
  /** `null` from an input that does not report a selection; read as "not at an edge". */
  readonly selectionStart: number | null;
  readonly selectionEnd: number | null;
  readonly valueLength: number;
}

function isWholeValueSelected(press: ArrowPress): boolean {
  return press.selectionStart === 0 && press.selectionEnd === press.valueLength;
}

function isCaretAtStart(press: ArrowPress): boolean {
  if (isWholeValueSelected(press)) return true;
  return press.selectionStart === 0 && press.selectionEnd === 0;
}

function isCaretAtEnd(press: ArrowPress): boolean {
  if (isWholeValueSelected(press)) return true;
  return press.selectionStart === press.valueLength && press.selectionEnd === press.valueLength;
}

/** What the table should do with one arrow press. */
export type ArrowOutcome =
  /** Take the key and move focus to `to`, selecting the value it finds there. */
  | { readonly kind: 'move'; readonly to: SetCellAddress }
  /** Take the key and do nothing with it — the grid ends here. Nothing moves, nothing collapses. */
  | { readonly kind: 'hold' }
  /** The key was never ours. The browser keeps it, default behaviour and all. */
  | { readonly kind: 'yield' };

const YIELD: ArrowOutcome = { kind: 'yield' };
const HOLD: ArrowOutcome = { kind: 'hold' };

function toward(to: SetCellAddress, exists: boolean): ArrowOutcome {
  return exists ? { kind: 'move', to } : HOLD;
}

/**
 * The Tab walk: one cell along the row, wrapping into the row beside it.
 *
 * `null` at the two ends of the whole grid, which is where the key goes back to the browser so the
 * table can be left — see the header for why that is not the mock's clamp.
 */
function walk(from: SetCellAddress, back: boolean, shape: SetGridShape): SetCellAddress | null {
  const lastColumn = shape.columnCount - 1;
  const lastRow = shape.rowCount - 1;
  if (back) {
    if (from.column > 0) return { row: from.row, column: from.column - 1 };
    if (from.row === 0) return null;
    return { row: from.row - 1, column: lastColumn };
  }
  if (from.column < lastColumn) return { row: from.row, column: from.column + 1 };
  if (from.row === lastRow) return null;
  return { row: from.row + 1, column: 0 };
}

/** What one navigation press means, pressed in the cell at `from` of a grid shaped `shape`. */
export function resolveArrowPress(
  from: SetCellAddress,
  press: ArrowPress,
  shape: SetGridShape,
): ArrowOutcome {
  // Tab reads Shift instead of yielding to it, so it is answered above the modifier gate. Any OTHER
  // modifier still hands the key straight back.
  if (press.key === 'Tab') {
    if (press.hasModifier && press.hasShift !== true) return YIELD;
    const to = walk(from, press.hasShift === true, shape);
    return to === null ? YIELD : { kind: 'move', to };
  }

  if (press.hasModifier) return YIELD;

  switch (press.key) {
    case 'ArrowUp':
      return toward({ row: from.row - 1, column: from.column }, from.row > 0);
    // Enter is DOWN — the mock's own `ArrowDown | Enter`. The cell has already committed by the time
    // this runs; see the header.
    case 'Enter':
    case 'ArrowDown':
      return toward(
        { row: from.row + 1, column: from.column },
        from.row < shape.rowCount - 1,
      );
    case 'ArrowLeft':
      if (!isCaretAtStart(press)) return YIELD;
      return toward({ row: from.row, column: from.column - 1 }, from.column > 0);
    case 'ArrowRight':
      if (!isCaretAtEnd(press)) return YIELD;
      return toward(
        { row: from.row, column: from.column + 1 },
        from.column < shape.columnCount - 1,
      );
    default:
      return YIELD;
  }
}
